import { orgStorage } from "@/utils/org-storage";
import storeUtils from "@/stores/utils";

function getFirstColumnKey(columns) {
    if (!columns?.length) {
        return null;
    }
    const col = columns[0];
    return col.field || col.value || "0";
}

/**
 * Keep the first column definition pinned: always visible, first in order, not hideable.
 */
export function enforceColumnConstraints(columnState, columns) {
    const firstKey = getFirstColumnKey(columns);
    if (!firstKey || !columnState?.length) {
        return columnState || [];
    }

    const state = columnState.map((col) =>
        col.key === firstKey
            ? { ...col, locked: true, visible: true }
            : col,
    );

    const visibleOthers = state
        .filter((col) => col.visible && col.key !== firstKey)
        .sort((a, b) => a.order - b.order);

    const orderMap = { [firstKey]: 0 };
    visibleOthers.forEach((col, index) => {
        orderMap[col.key] = index + 1;
    });

    let hiddenOrder = visibleOthers.length + 1;
    return state.map((col) => {
        if (orderMap[col.key] !== undefined) {
            return { ...col, order: orderMap[col.key] };
        }
        if (!col.visible) {
            const updated = { ...col, order: hiddenOrder };
            hiddenOrder += 1;
            return updated;
        }
        return col;
    });
}

/** Phone width: keep a short default set; the column menu can show the rest. */
const PHONE_BREAKPOINT = "xs";
/** Layouts whose defaults differ from desktop, so prefs are stored apart. */
const LAYOUT_SPECIFIC_BREAKPOINTS = new Set(["xs", "sm"]);
const PHONE_MAX_DATA_COLUMNS = 4;

const STOCK_LOCATION_FIELDS = new Set(["stock_location", "location_cell"]);

const STATUS_FIELDS = new Set([
    "status",
    "order_status",
    "shipment_status",
    "receipt_status",
    "agent_commission_status",
    "invoice_status",
    "sale_status",
]);

const PARTY_FIELDS = new Set([
    "name",
    "label",
    "full_name",
    "product_name",
    "buyer_name",
    "customer_name",
    "provider_name",
    "billed_name",
    "biller_name",
    "seller",
    "recipient",
    "owner",
    "initiator",
    "vendor",
    "contact",
]);

const AMOUNT_FIELDS = new Set([
    "total_amount_excl_vat",
    "total_purchase_price_excl_vat",
    "invoice_amount",
    "total_pos",
    "unit_amount",
    "unit_amount_excl_vat",
    "unit_purchase_price_excl_vat",
]);

const QUANTITY_FIELDS = new Set([
    "count",
    "quantity",
    "stock_count",
    "combined_stock",
    "received_count",
    "items_count",
    "products_count",
]);

const CODE_FIELDS = new Set([
    "upc",
    "product_upc",
    "code",
]);

function columnKey(col, index) {
    return col.field || col.value || String(index);
}

function isUtilityColumn(col) {
    return (
        col.type === "actions"
        || col.type === "getactions"
        || col.type === "_orderingfield"
        || col.field === "*"
    );
}

function isExplicitlyHiddenOnBreakpoint(col, breakpoint) {
    return !!(
        breakpoint
        && Array.isArray(col.hide)
        && col.hide.indexOf(breakpoint) !== -1
    );
}

/**
 * Data columns shown by default on a phone.
 * Explicit `hide: ["xs"]` stays out. Otherwise: identity, stock location, status, one party,
 * one amount, one quantity, one code, then column order, capped at 4.
 * Tables that already expose 4 or fewer candidates keep all of them.
 */
function phoneDefaultVisibleKeys(columns) {
    const eligible = [];
    (columns || []).forEach((col, index) => {
        if (col.hidden === true || isUtilityColumn(col)) {
            return;
        }
        if (isExplicitlyHiddenOnBreakpoint(col, PHONE_BREAKPOINT)) {
            return;
        }
        eligible.push({ col, index, key: columnKey(col, index) });
    });
    if (eligible.length <= PHONE_MAX_DATA_COLUMNS) {
        return new Set(eligible.map((item) => item.key));
    }

    const pickedKeys = new Set();
    const add = (item) => {
        if (!item || pickedKeys.has(item.key) || pickedKeys.size >= PHONE_MAX_DATA_COLUMNS) {
            return;
        }
        pickedKeys.add(item.key);
    };
    const firstMatch = (fields) => eligible.find((item) => fields.has(item.col.field));

    add(eligible[0]);
    add(firstMatch(STOCK_LOCATION_FIELDS));
    add(firstMatch(STATUS_FIELDS));
    add(firstMatch(PARTY_FIELDS));
    add(firstMatch(AMOUNT_FIELDS));
    add(firstMatch(QUANTITY_FIELDS));
    add(firstMatch(CODE_FIELDS));
    eligible.forEach(add);
    return pickedKeys;
}

/**
 * Build default column visibility/order state from table column definitions.
 * `hide` on a column is the default for that breakpoint, not a hard cut:
 * the column menu can show it again. On phones, wide tables also start
 * with a short set of columns.
 *
 * @param {Array} columns
 * @param {string|null} breakpoint Vuetify breakpoint name (`xs`, `sm`, ...)
 */
export function buildDefaultColumnState(columns, breakpoint = null) {
    const phoneKeys = breakpoint === PHONE_BREAKPOINT
        ? phoneDefaultVisibleKeys(columns)
        : null;
    const state = (columns || []).map((col, index) => ({
        key: columnKey(col, index),
        label: col.label || col.text || col.field || "",
        visible: !isColumnHiddenByDefault(col, index, breakpoint, phoneKeys),
        order: index,
        locked:
            index === 0 || col.locked === true || col.type === "actions",
    }));
    return enforceColumnConstraints(state, columns);
}

function isColumnHiddenByDefault(col, index, breakpoint, phoneKeys) {
    if (col.hidden === true) {
        return true;
    }
    if (isExplicitlyHiddenOnBreakpoint(col, breakpoint)) {
        return true;
    }
    if (breakpoint !== PHONE_BREAKPOINT || isUtilityColumn(col)) {
        return false;
    }
    return !phoneKeys.has(columnKey(col, index));
}

/**
 * Apply column state to raw column definitions for header rendering.
 */
export function applyColumnState(columns, columnState) {
    if (!columnState?.length) {
        return columns || [];
    }
    const firstKey = getFirstColumnKey(columns);
    const stateMap = {};
    columnState.forEach((item) => {
        stateMap[item.key] = item;
    });
    const ordered = [...(columns || [])]
        .map((col, index) => {
            const key = columnKey(col, index);
            const state = stateMap[key];
            if (state && !state.visible && key !== firstKey) {
                return null;
            }
            return {
                col,
                order: state?.order ?? 999,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.order - b.order)
        .map((item) => item.col);
    return ordered;
}

export function isColumnStateCustomized(columnState, columns, breakpoint = null) {
    const defaults = buildDefaultColumnState(columns, breakpoint);
    if (defaults.length !== columnState.length) {
        return true;
    }
    for (let i = 0; i < defaults.length; i++) {
        const def = defaults[i];
        const cur = columnState.find((c) => c.key === def.key);
        if (!cur) {
            return true;
        }
        if (cur.visible !== def.visible || cur.order !== def.order) {
            return true;
        }
    }
    return false;
}

function legacyColumnStorageKey(scope) {
    if (!scope) {
        return null;
    }
    return `safetee.table.columns.${scope}`;
}

function readLegacyColumnState(scope, columns) {
    const legacyKey = legacyColumnStorageKey(scope);
    if (!legacyKey) {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(legacyKey);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export function loadColumnState(storageKey, columns, scope = null, breakpoint = null) {
    if (!storageKey || typeof window === "undefined") {
        return buildDefaultColumnState(columns, breakpoint);
    }
    try {
        let parsed = null;
        const raw = orgStorage.getItem(storageKey);
        if (raw) {
            parsed = JSON.parse(raw);
        } else if (scope && !LAYOUT_SPECIFIC_BREAKPOINTS.has(breakpoint)) {
            parsed = readLegacyColumnState(scope, columns);
        }
        if (!Array.isArray(parsed)) {
            return buildDefaultColumnState(columns, breakpoint);
        }
        const defaults = buildDefaultColumnState(columns, breakpoint);
        const merged = defaults.map((def) => {
            const saved = parsed.find((p) => p.key === def.key);
            if (!saved) {
                return def;
            }
            return {
                ...def,
                visible: saved.visible ?? def.visible,
                order: saved.order ?? def.order,
            };
        });
        return enforceColumnConstraints(merged, columns);
    } catch {
        return buildDefaultColumnState(columns, breakpoint);
    }
}

export function saveColumnState(storageKey, columnState) {
    if (!storageKey || typeof window === "undefined") {
        return;
    }
    try {
        orgStorage.setItem(
            storageKey,
            JSON.stringify(
                columnState.map(({ key, visible, order }) => ({
                    key,
                    visible,
                    order,
                })),
            ),
        );
    } catch {
        // ignore quota errors
    }
}

/**
 * Storage key for column layout, prefixed via orgStorage like filter keys.
 */
export function columnStorageKey(scope) {
    return storeUtils.getColumnStorageKey(scope);
}

/**
 * Desktop shares one layout. Phone (`xs`) and small (`sm`) keep their own,
 * so a wide-screen "show everything" choice does not override mobile defaults.
 */
export function columnStorageKeyForLayout(scope, breakpoint) {
    const base = columnStorageKey(scope);
    if (!base || !LAYOUT_SPECIFIC_BREAKPOINTS.has(breakpoint)) {
        return base;
    }
    return `${base}-${breakpoint}`;
}
